// @adr SPARK-0055-document-displays-use-one-public-boundary@3: Linked documents use the shared viewer, not a page-local iframe.
import { useMemo } from "react";
import { FormattedMessage, useIntl } from "react-intl";
import { useApolloClient, useQuery } from "@apollo/client/react";
import { Dialog } from "@/components/common/Dialog";
import { OrganizationalRecordLinkedDocumentDocument, OrganizationalRecordLinkedEntryDocument, OrganizationalRecordDocumentContentDocument,
  OrganizationalRecordItemKind } from "@/graphql/generated/operations";
import { userErrorMessage } from "@/lib/user-error";
import { base64ToBlob } from "@/lib/use-open-document";
import { createDocumentResource } from "@/lib/document-resource";
import { DocumentView } from "@/components/documents/DocumentView";
import { OrganizationalRecordBacklinks, OrganizationalRecordLinkList, type OrganizationalRecordReference, type OrganizationalRecordNavigate } from "./OrganizationalRecordLinks";
import { OrganizationalRecordComments } from "./OrganizationalRecordComments";


export function OrganizationalRecordItemDialog({ recordId, item, canWrite, onClose, onNavigate }: {
  recordId: string; item: OrganizationalRecordReference; canWrite: boolean; onClose: () => void; onNavigate: OrganizationalRecordNavigate;
}) {
  return <Dialog id="organizational-record-item" open onClose={onClose} dismiss="light" maxWidth="lg" title={<FormattedMessage id={item.kind === OrganizationalRecordItemKind.Comment ? "organizationalRecord.links.comment" : "organizationalRecord.links.details"} />}>
    {item.kind === OrganizationalRecordItemKind.Document ? <DocumentDetail recordId={recordId} item={item} onNavigate={onNavigate} />
      : item.kind === OrganizationalRecordItemKind.Entry ? <EntryDetail recordId={recordId} item={item} canWrite={canWrite} onNavigate={onNavigate} />
      : <OrganizationalRecordComments recordId={recordId} entryId={item.entryId ?? undefined} focusId={item.id} canWrite={canWrite} onNavigate={onNavigate} />}
  </Dialog>;
}

function EntryDetail({ recordId, item, canWrite, onNavigate }: { recordId: string; item: OrganizationalRecordReference; canWrite: boolean; onNavigate: OrganizationalRecordNavigate }) {
  const intl = useIntl();
  const query = useQuery(OrganizationalRecordLinkedEntryDocument, { variables: { id: item.id }, fetchPolicy: "no-cache" });
  const entry = query.data?.organizationalRecordEntry;
  if (query.loading) return <p><FormattedMessage id="common.loading" /></p>;
  if (query.error) return <p role="alert">{userErrorMessage(intl, query.error)}</p>;
  if (!entry || entry.recordId !== recordId) return <p role="alert"><FormattedMessage id="organizationalRecord.links.unavailable" /></p>;
  return <div className="space-y-4">
    <h3 className="smycles-field-value font-medium break-words">{entry.title}</h3>
    <p className="smycles-field-value whitespace-pre-wrap break-words">{entry.content}</p>
    <OrganizationalRecordPerson person={entry.createdBy} />
    <OrganizationalRecordLinkList links={entry.links} onNavigate={onNavigate} />
    <OrganizationalRecordBacklinks recordId={recordId} target={item} onNavigate={onNavigate} />
    <OrganizationalRecordComments recordId={recordId} entryId={item.id} canWrite={canWrite} onNavigate={onNavigate} />
  </div>;
}

function DocumentDetail({ recordId, item, onNavigate }: { recordId: string; item: OrganizationalRecordReference; onNavigate: OrganizationalRecordNavigate }) {
  const intl = useIntl();
  const query = useQuery(OrganizationalRecordLinkedDocumentDocument, { variables: { id: item.id }, fetchPolicy: "no-cache" });
  const document = query.data?.organizationalRecordDocument;
  if (query.loading) return <p><FormattedMessage id="common.loading" /></p>;
  if (query.error) return <p role="alert">{userErrorMessage(intl, query.error)}</p>;
  if (!document || document.recordId !== recordId) return <p role="alert"><FormattedMessage id="organizationalRecord.links.unavailable" /></p>;
  return <div className="space-y-4">
    <h3 className="smycles-field-value font-medium break-words">{document.title}</h3>
    <DocumentFile id={document.id} fileName={document.fileName} title={document.title} />
    <OrganizationalRecordBacklinks recordId={recordId} target={item} onNavigate={onNavigate} />
  </div>;
}

function DocumentFile({ id, fileName }: { id: string; fileName: string; title: string }) {
  const client = useApolloClient();
  const resource = useMemo(() => createDocumentResource({ name: fileName, load: async () => {
    const { data } = await client.query({ query: OrganizationalRecordDocumentContentDocument, variables: { id }, fetchPolicy: "no-cache" });
    const file = data?.organizationalRecordDocument?.content;
    if (!file?.content) throw new Error("Missing document bytes");
    return base64ToBlob(file.content, file.mimeType ?? "application/octet-stream");
  } }), [client, id, fileName]);
  return <DocumentView variant="viewer" file={resource} />;
}

import { OrganizationalRecordPerson } from "./OrganizationalRecordPerson";
